import React, { useState } from 'react';
import { MessageSquare, X, Bug, Lightbulb, Activity } from 'lucide-react';
import { useTestStore } from '../../store/useTestStore';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-hot-toast';

const FeedbackWidget: React.FC = () => {
    const { isWidgetOpen, setWidgetOpen, createTicket, isLoading } = useTestStore();
    const { user } = useAuth();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('bug');
    const [severity, setSeverity] = useState('medium');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user) return;

        try {
            await createTicket({
                title,
                description: [{ children: [{ text: description }] }], // Slate JSON format simple
                category,
                severity,
                status: 'open',
                reportedBy: user._id, // Assuming user.id maps to the user ID Payload needs
                context: {
                    url: window.location.href,
                    userAgent: navigator.userAgent,
                }
            });
            toast.success('Feedback envoyé ! Merci !');
            setWidgetOpen(false);
            setTitle('');
            setDescription('');
        } catch (error) {
            toast.error('Erreur lors de l\'envoi du feedback.');
        }
    };

    if (!user) return null;

    return (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
            {isWidgetOpen && (
                <div className="mb-4 w-96 bg-gray-900 border border-gold/30 rounded-lg shadow-2xl p-6 backdrop-blur-sm animate-in slide-in-from-bottom-5">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-gold font-serif text-lg">Feedback & Bug Report</h3>
                        <button onClick={() => setWidgetOpen(false)} className="text-gray-400 hover:text-white">
                            <X size={20} />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Titre</label>
                            <input
                                type="text"
                                className="w-full bg-black/50 border border-gray-700 rounded px-3 py-2 text-white focus:border-gold focus:outline-none"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                required
                            />
                        </div>

                        <div className="flex gap-4">
                            <div className="flex-1">
                                <label className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Type</label>
                                <select
                                    className="w-full bg-black/50 border border-gray-700 rounded px-3 py-2 text-white"
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                >
                                    <option value="bug">Bug</option>
                                    <option value="suggestion">Suggestion</option>
                                    <option value="performance">Performance</option>
                                </select>
                            </div>
                            <div className="flex-1">
                                <label className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Sévérité</label>
                                <select
                                    className="w-full bg-black/50 border border-gray-700 rounded px-3 py-2 text-white"
                                    value={severity}
                                    onChange={(e) => setSeverity(e.target.value)}
                                >
                                    <option value="low">Mineur</option>
                                    <option value="medium">Moyen</option>
                                    <option value="high">Majeur</option>
                                    <option value="critical">Critique</option>
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Description</label>
                            <textarea
                                className="w-full bg-black/50 border border-gray-700 rounded px-3 py-2 text-white h-24 resize-none focus:border-gold focus:outline-none"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                required
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full bg-gold text-black font-bold py-2 rounded hover:bg-gold-light transition-colors disabled:opacity-50"
                        >
                            {isLoading ? 'Envoi...' : 'Envoyer le rapport'}
                        </button>
                    </form>
                </div>
            )}

            <button
                onClick={() => setWidgetOpen(!isWidgetOpen)}
                className="bg-gold text-black p-4 rounded-full shadow-lg hover:scale-105 transition-transform flex items-center justify-center"
            >
                {isWidgetOpen ? <X size={24} /> : <MessageSquare size={24} />}
            </button>
        </div>
    );
};

export default FeedbackWidget;
